import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import filepicker from "filepicker-js";
import * as filestack from "filestack-js";
import { filestackEndpoints, sanitizeUrl } from "helpers/filestackUrl";
import {
  S3_URL,
  FILEPICKER_API_KEY,
  FILESTACK_IMAGE_PATH,
  FILESTACK_BUCKET,
} from "helpers/constants";
import LoadingSpinner from "components/LoadingSpinner";
import Placeholder from "components/Placeholder";
import allowedImageMimetypes from "helpers/allowedImageMimetypes";

import "./_style.scss";

const client = filestack.init(FILEPICKER_API_KEY);

const SingleImageInput = ({
  value,
  initialValue,
  alt,
  isInvalid,
  imageWidth,
  imageHeight,
  cropRatio,
  placeholder,
  placeholderText,
  onChange,
  onChangeComplete,
  maxFileSize = 4.5 * 1024 * 1024, // 4.5 MB
  maxImageDimensions = [4000, 4000],
  children, // optional trigger element
}) => {
  filepicker.setKey(FILEPICKER_API_KEY);
  const [imgSrc, setImgSrc] = useState(value || initialValue);
  const [isStoring, setIsStoring] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    setImgSrc(value || initialValue);
  }, [value, initialValue]);

  const onUploadDone = async (Blob) => {
    try {
      setIsStoring(true);

      const uploaded = Blob?.filesUploaded?.[0];
      if (!uploaded) {
        setIsStoring(false);
        return;
      }

      // Transform image
      const processedUrl = client.transform(
        uploaded.handle,
        { resize: { width: imageWidth, height: imageHeight, fit: "max" } },
        { preserve: true },
      );

      // Store
      const storeUrl = filestackEndpoints(imageWidth, cropRatio || 1, {
        ...uploaded,
        url: processedUrl,
      });
      const sanitizedFilename = sanitizeUrl(uploaded.key);

      const s3Blob = await new Promise((resolve, reject) => {
        try {
          filepicker.storeUrl(
            storeUrl,
            {
              filename: sanitizedFilename,
              path: FILESTACK_IMAGE_PATH,
            },
            (result) =>
              (result?.key ? resolve(result) : reject(new Error("Failed to store image"))),
            (error) => reject(error instanceof Error ? error : new Error(String(error))),
          );
        } catch (e) {
          reject(e);
        }
      });

      const finalUrl = `${S3_URL}${s3Blob.key}`;
      setImgSrc(finalUrl);
      onChange?.(finalUrl);
      onChangeComplete?.();
    } catch (err) {
      console.error("Upload/transform/store error:", err);
      if (err?.message?.includes("maximum accepted image size") || err?.status === 400) {
        setErrorMessage(
          "This image is too large. Please upload an image smaller than 100 megapixels.",
        );
      } else {
        setErrorMessage("Something went wrong while uploading your image. Please try again.");
      }
    } finally {
      setIsStoring(false);
    }
  };

  const openPicker = () => {
    try {
      const picker = client.picker({
        fromSources: ["local_file_system"],
        maxFiles: 1,
        imageMax: maxImageDimensions,
        accept: allowedImageMimetypes,
        customText: {
          "Select Files to Upload": "Select File to Upload",
          "or Drag and Drop, Copy and Paste Files": "or Drag and Drop, Copy and Paste File",
        },
        storeTo: { location: "s3", container: FILESTACK_BUCKET, region: "ap-southeast-2" },
        transformations: {
          crop: { aspectRatio: cropRatio ?? 0, force: !!cropRatio },
          rotate: true,
        },
        onUploadDone,
        onUploadStarted: () => {
          setErrorMessage(null);
        },
        onFileUploadFailed: (file, error) => {
          console.error("File upload failed:", file, error);
          setIsStoring(false);
          setErrorMessage("This file is too large or invalid. Please choose a smaller image.");
        },
        onFileSelected: (file) => {
          if (file.size > maxFileSize) {
            setErrorMessage(
              `This file must be less than ${(maxFileSize / 1_048_576).toFixed(2)}MB`,
            );
            picker.close();
          }
          const safeName = file.filename.replace(/[^a-zA-Z0-9_.-]/g, "").trim();
          // Fallback to timestamp if name becomes empty - should have extension though
          const newName = safeName || `img_${Date.now()}`;
          return { ...file, filename: newName };
        },
      });

      picker.open();
    } catch (e) {
      console.error("Picker open error:", e);
    }
  };

  return (
    <div className="single-image-uploader__wrapper">
      <div className={classNames("single-image-uploader", { "--isInvalid": isInvalid })}>
        {imgSrc ? (
          <div className="single-image-uploader__thumb" onClick={openPicker}>
            <img src={imgSrc} alt={alt} style={{ maxWidth: imageWidth, maxHeight: imageHeight }} />
            {isStoring && (
              <div className="single-image-uploader__spinner">
                <LoadingSpinner />
              </div>
            )}
          </div>
        ) : (
          <div className="single-image-uploader__placeholder" onClick={openPicker}>
            {placeholder || <Placeholder text={placeholderText || "Select image"} />}
          </div>
        )}

        {/* If children are provided, render them and attach openPicker */}
        {children && (
          <div onClick={openPicker} className="single-image-uploader__custom-trigger">
            {children}
          </div>
        )}
      </div>
      {errorMessage && <div className="single-image-uploader__error">{errorMessage}</div>}
    </div>
  );
};

SingleImageInput.propTypes = {
  value: PropTypes.string,
  initialValue: PropTypes.string,
  alt: PropTypes.string,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  cropRatio: PropTypes.number,
  isInvalid: PropTypes.bool,
  placeholder: PropTypes.node,
  onChange: PropTypes.func,
  onChangeComplete: PropTypes.func,
  maxFileSize: PropTypes.number,
  maxImageDimensions: PropTypes.array,
};

export default SingleImageInput;
